import React, { useState } from 'react';
import Creatable from 'react-select/creatable';

/**
 * Wrapper around react-select's Creatable that auto-commits any typed-but-unsaved
 * text as a new custom option when the user clicks away (blurs) from the dropdown.
 *
 * By default, Creatable discards typed text on blur — the user must press Enter or
 * click the "Add ..." dropdown option to commit. This wrapper catches the blur event,
 * checks for pending input, and fires onChange with the new option appended so
 * clinicians don't silently lose their typed content.
 *
 * Duplicate values are prevented: if the typed text matches an already-selected
 * option, it is ignored rather than creating a duplicate chip.
 *
 * All other Creatable props (isMulti, options, styles, etc.) are forwarded unchanged.
 */
function AutoSaveCreatable({ onChange, value, ...props }) {
  const [pendingInput, setPendingInput] = useState('');

  return (
    <Creatable
      {...props}
      value={value}
      inputValue={pendingInput}
      onChange={(newValue, actionMeta) => {
        setPendingInput('');
        onChange(newValue, actionMeta);
      }}
      onInputChange={(newValue, actionMeta) => {
        if (actionMeta.action === 'input-change') {
          setPendingInput(newValue);
        } else if (actionMeta.action === 'input-blur') {
          const trimmed = pendingInput.trim();
          if (trimmed && !value?.some(opt => opt.value === trimmed)) {
            const newOption = { value: trimmed, label: trimmed };
            onChange([...(value || []), newOption], { action: 'create-option' });
          }
          setPendingInput('');
        } else {
          setPendingInput('');
        }
      }}
    />
  );
}

export default AutoSaveCreatable;
